// The global loader's morph: one shape that flows through ten studio silhouettes (a heart, a
// dumbbell, a running shoe...), turning a little with every step. Each silhouette is cast on the
// same rays from its origin, so point i of one shape travels straight to point i of the next and
// the morph never twists or folds.
//
// The shapes are CSS clip-path polygons played as CSS / Web Animations, not SVG SMIL: browsers
// start SMIL only after the page's load event, so a loader shown while a page opens stood still.

// The loader's box, in percent: every point stays inside it.
const BOX = 100;

// Points per outline: enough for a smooth curve at the loader's size.
const POINT_COUNT = 120;
// Rays an outline is first cast on, before its corners are softened.
const RAY_COUNT = 360;
const RAY_SLACK = 1e-6;
// The box (in view-box units) every shape is fitted into, before its optical scale.
const FIT_SIZE = 70;
// Whole milliseconds keep the timings free of float noise like 11.000000000000002s.
const STEP_MILLISECONDS = 1400;
// Ten steps of 108 degrees make three whole turns, so the loop has no jump.
const STEP_TURN = 108;
// Slow out of one shape, quick through the middle, slow into the next: each shape rests a beat.
const STEP_EASING = "cubic-bezier(0.65, 0, 0.35, 1)";
// The first loader starts this far into a step, past the slow start of the easing, so it moves at once.
const LEAD_IN = 0.4;

type Point = [number, number];

type Outline = Point[];

interface ILoaderShape {
  // Closed outlines, y pointing down; the shape is everything they cover, seen from its origin.
  parts: Outline[];
  // Where the rays start: a point every part can be seen from. The middle of the parts' box if unset.
  origin?: Point;
  // Degrees the corners are rounded over; 0 keeps them as drawn.
  softness: number;
  // Pointy shapes look smaller than round ones in the same box.
  scale: number;
}

export interface ILoaderMorph {
  // Each shape as a CSS clip-path polygon, already turned back by the loader's turn at its step.
  shapes: string[];
  // One keyframe per shape and the first one again, so the cycle loops without a jump.
  morphKeyframes: Keyframe[];
  turnKeyframes: Keyframe[];
  easing: string;
  stepMilliseconds: number;
  cycleMilliseconds: number;
  leadInMilliseconds: number;
  stepTurn: number;
}

const TAU = Math.PI * 2;
const OUTLINE_SAMPLES = 360;

const traced = (at: (turn: number) => Point): Outline =>
  Array.from({ length: OUTLINE_SAMPLES }, (_, index) => at((index / OUTLINE_SAMPLES) * TAU));

const ellipse = ([centreX, centreY]: Point, radiusX: number, radiusY = radiusX): Outline =>
  traced((t) => [centreX + radiusX * Math.cos(t), centreY + radiusY * Math.sin(t)]);

const roundedRect = (left: number, top: number, right: number, bottom: number, radius: number): Outline =>
  (
    [
      [right - radius, top + radius],
      [right - radius, bottom - radius],
      [left + radius, bottom - radius],
      [left + radius, top + radius],
    ] as Point[]
  ).flatMap(([cornerX, cornerY], corner) =>
    Array.from({ length: 9 }, (_, index): Point => {
      const angle = -Math.PI / 2 + ((corner + index / 8) * Math.PI) / 2;
      return [cornerX + radius * Math.cos(angle), cornerY + radius * Math.sin(angle)];
    })
  );

// A petal or a leaf: pointed at both ends, growing from `base` towards `angle`
// (degrees clockwise from up), `width` wide at its widest.
const petal = ([baseX, baseY]: Point, angle: number, length: number, width: number): Outline => {
  const radians = (angle * Math.PI) / 180;
  return traced((t) => {
    const along = (length / 2) * (1 - Math.cos(t));
    const across = (width / 2) * Math.sign(Math.sin(t)) * Math.abs(Math.sin(t)) ** 1.5;
    return [
      baseX + along * Math.sin(radians) + across * Math.cos(radians),
      baseY - along * Math.cos(radians) + across * Math.sin(radians),
    ];
  });
};

// A studio's day in ten silhouettes. Round and angular ones take turns, so every step changes a lot.
const LOADER_SHAPES: ILoaderShape[] = [
  // Heart.
  {
    parts: [
      traced((t) => [
        16 * Math.sin(t) ** 3,
        -(13 * Math.cos(t) - 5 * Math.cos(2 * t) - 2 * Math.cos(3 * t) - Math.cos(4 * t)),
      ]),
    ],
    softness: 3,
    scale: 1,
  },
  // Calendar: the schedule.
  {
    parts: [
      roundedRect(-1, -0.72, 1, 1, 0.3),
      roundedRect(-0.6, -1.05, -0.36, -0.45, 0.12),
      roundedRect(0.36, -1.05, 0.6, -0.45, 0.12),
    ],
    softness: 0,
    scale: 0.92,
  },
  // Apple.
  {
    parts: [
      ellipse([-0.3, 0.15], 0.66, 0.72),
      ellipse([0.3, 0.15], 0.66, 0.72),
      petal([0.02, -0.5], 48, 0.62, 0.34),
      roundedRect(-0.05, -0.82, 0.05, -0.45, 0.04),
    ],
    origin: [0, 0.15],
    softness: 2,
    scale: 1,
  },
  // Dumbbell.
  {
    parts: [
      roundedRect(-0.9, -0.13, 0.9, 0.13, 0.06),
      roundedRect(-0.76, -0.56, -0.44, 0.56, 0.12),
      roundedRect(0.44, -0.56, 0.76, 0.56, 0.12),
      roundedRect(-1, -0.36, -0.7, 0.36, 0.08),
      roundedRect(0.7, -0.36, 1, 0.36, 0.08),
    ],
    origin: [0, 0],
    softness: 1,
    scale: 1,
  },
  // Water bottle.
  {
    parts: [
      roundedRect(-0.5, -0.42, 0.5, 1, 0.22),
      roundedRect(-0.24, -0.78, 0.24, -0.3, 0.08),
      roundedRect(-0.32, -1, 0.32, -0.72, 0.1),
    ],
    softness: 1,
    scale: 1,
  },
  // Running shoe, toe to the right.
  {
    parts: [
      [
        [-1, 0.45],
        [0.95, 0.45],
        [1.08, 0.32],
        [1.02, 0.12],
        [0.75, -0.02],
        [0.3, -0.2],
        [-0.05, -0.42],
        [-0.18, -0.62],
        [-0.3, -0.62],
        [-0.45, -0.42],
        [-0.62, -0.4],
        [-0.92, -0.62],
        [-1.02, -0.5],
        [-1.06, 0.2],
      ],
    ],
    softness: 3,
    scale: 1.05,
  },
  // Alarm clock: the class reminder.
  {
    parts: [
      ellipse([0, 0.1], 0.78),
      ellipse([-0.58, -0.62], 0.26),
      ellipse([0.58, -0.62], 0.26),
      roundedRect(-0.07, -0.92, 0.07, -0.6, 0.04),
      ellipse([0, -0.94], 0.1),
      ellipse([-0.5, 0.86], 0.12, 0.16),
      ellipse([0.5, 0.86], 0.12, 0.16),
    ],
    origin: [0, 0.1],
    softness: 1,
    scale: 1,
  },
  // Lightning: energy.
  {
    parts: [
      [
        [0.28, -1],
        [-0.58, 0.14],
        [-0.04, 0.14],
        [-0.28, 1],
        [0.6, -0.16],
        [0.06, -0.16],
      ],
    ],
    softness: 2,
    scale: 1.05,
  },
  // Lotus: yoga.
  {
    parts: [
      petal([0, 0.6], 0, 1.5, 0.95),
      petal([0, 0.6], -46, 1.2, 0.8),
      petal([0, 0.6], 46, 1.2, 0.8),
      petal([0, 0.62], -84, 0.9, 0.55),
      petal([0, 0.62], 84, 0.9, 0.55),
    ],
    origin: [0, 0.4],
    softness: 3,
    scale: 1.02,
  },
  // Medal: a reached goal.
  {
    parts: [
      ellipse([0, 0.38], 0.6),
      [
        [-0.66, -1],
        [-0.28, -1],
        [0.1, -0.12],
        [-0.28, -0.12],
      ],
      [
        [0.28, -1],
        [0.66, -1],
        [0.28, -0.12],
        [-0.1, -0.12],
      ],
    ],
    origin: [0, 0.38],
    softness: 1,
    scale: 1,
  },
];

// How far from the centre each ray leaves the outline (the farthest crossing). Ray i points at
// i / RAY_COUNT of a turn plus `turn` radians. Each edge only meets the rays inside the angle it
// spans as seen from the centre, so this stays quick on the first loader of a page.
const castRays = (outline: Point[], [centreX, centreY]: Point, turn: number) => {
  const radii = new Array<number>(RAY_COUNT).fill(0);
  const rayStep = TAU / RAY_COUNT;

  outline.forEach(([startX, startY], index) => {
    const [endX, endY] = outline[(index + 1) % outline.length];
    const offsetX = startX - centreX;
    const offsetY = startY - centreY;
    const edgeX = endX - startX;
    const edgeY = endY - startY;
    const startAngle = Math.atan2(offsetY, offsetX) - turn;
    // The edge's angle seen from the centre, the short way round.
    const sweep = ((Math.atan2(endY - centreY, endX - centreX) - turn - startAngle + 3 * Math.PI) % TAU) - Math.PI;
    // A ray through a corner belongs to both of its edges; the slack keeps rounding errors from
    // letting it slip between them.
    const firstRay = Math.ceil(Math.min(startAngle, startAngle + sweep) / rayStep - RAY_SLACK);
    const lastRay = Math.floor(Math.max(startAngle, startAngle + sweep) / rayStep + RAY_SLACK);

    for (let ray = firstRay; ray <= lastRay; ray++) {
      const angle = ray * rayStep + turn;
      const denominator = Math.cos(angle) * edgeY - Math.sin(angle) * edgeX;
      if (denominator === 0) continue;

      const slot = ((ray % RAY_COUNT) + RAY_COUNT) % RAY_COUNT;
      radii[slot] = Math.max(radii[slot], (offsetX * edgeY - offsetY * edgeX) / denominator);
    }
  });

  return radii;
};

// Rounds the corners by blurring the radius around the circle.
const soften = (radii: number[], softness: number) => {
  const spread = (softness * radii.length) / 360;
  if (spread === 0) return radii;

  const reach = Math.ceil(spread * 3);
  const weights = Array.from({ length: reach * 2 + 1 }, (_, index) =>
    Math.exp(-((index - reach) ** 2) / (2 * spread ** 2))
  );
  const weightSum = weights.reduce((sum, weight) => sum + weight, 0);

  return radii.map((_, index) => {
    let sum = 0;
    for (let offset = 0; offset < weights.length; offset++) {
      sum += weights[offset] * radii[(index + offset - reach + radii.length) % radii.length];
    }
    return sum / weightSum;
  });
};

const round = (value: number) => Number(value.toFixed(1));

// The shape as a clip-path, already turned back by the loader's turn at this step, so it rests upright.
const toPolygon = ({ parts, origin, softness, scale }: ILoaderShape, turn: number) => {
  const xs = parts.flat().map(([x]) => x);
  const ys = parts.flat().map(([, y]) => y);
  const width = Math.max(...xs) - Math.min(...xs);
  const height = Math.max(...ys) - Math.min(...ys);
  const boxMiddle: Point = [(Math.max(...xs) + Math.min(...xs)) / 2, (Math.max(...ys) + Math.min(...ys)) / 2];
  const rayOrigin = origin ?? boxMiddle;
  const size = (FIT_SIZE * scale) / Math.max(width, height);
  const turnRadians = (turn * Math.PI) / 180;

  // The farthest any part reaches along each ray.
  const radii = soften(
    parts
      .map((part) => castRays(part, rayOrigin, turnRadians))
      .reduce((farthest, partRadii) => farthest.map((radius, index) => Math.max(radius, partRadii[index]))),
    softness
  );
  // The box middle goes to the middle of the loader, so the rays start this far from it (turned back too).
  const originX = (rayOrigin[0] - boxMiddle[0]) * size;
  const originY = (rayOrigin[1] - boxMiddle[1]) * size;
  const shiftX = originX * Math.cos(turnRadians) + originY * Math.sin(turnRadians);
  const shiftY = originY * Math.cos(turnRadians) - originX * Math.sin(turnRadians);
  const everyNth = RAY_COUNT / POINT_COUNT;

  const points = Array.from({ length: POINT_COUNT }, (_, index) => {
    const angle = (index / POINT_COUNT) * TAU;
    const radius = radii[index * everyNth] * size;
    const middle = BOX / 2;
    return `${round(middle + shiftX + radius * Math.cos(angle))}% ${round(middle + shiftY + radius * Math.sin(angle))}%`;
  });

  return `polygon(${points.join(", ")})`;
};

const buildLoaderMorph = (): ILoaderMorph => {
  const steps = LOADER_SHAPES.length;
  const shapes = LOADER_SHAPES.map((shape, index) => toPolygon(shape, index * STEP_TURN));
  const frames = [...shapes, shapes[0]];

  return {
    shapes,
    morphKeyframes: frames.map((clipPath) => ({ clipPath, easing: STEP_EASING })),
    turnKeyframes: frames.map((_, index) => ({ transform: `rotate(${index * STEP_TURN}deg)`, easing: STEP_EASING })),
    easing: STEP_EASING,
    stepMilliseconds: STEP_MILLISECONDS,
    cycleMilliseconds: STEP_MILLISECONDS * steps,
    leadInMilliseconds: Math.round(STEP_MILLISECONDS * LEAD_IN),
    stepTurn: STEP_TURN,
  };
};

let loaderMorph: ILoaderMorph | undefined;

export const getLoaderMorph = () => {
  loaderMorph ??= buildLoaderMorph();
  return loaderMorph;
};
